import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Sun, Loader2, AlertTriangle, Clock, Flag, User, Hand, Play, Pause,
  ChevronRight, Star, MoonStar, ShieldCheck, Sparkles, Zap,
} from 'lucide-react';
import { myDay, claimTask, startTimer, stopTimer, decideSnooze, listUsers, getPriorityWeights, setPriorityWeights } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth, isSuperAdmin } from '../../context/AuthContext';
import TaskDetailDialog from '../../components/crm/TaskDetailDialog';

const PRI = {
  urgent: { color: 'bg-red-600', text: 'text-red-700' },
  high: { color: 'bg-orange-500', text: 'text-orange-700' },
  medium: { color: 'bg-[#0f5c3d]', text: 'text-emerald-700' },
  low: { color: 'bg-gray-400', text: 'text-gray-600' },
};

const BUCKET_META = {
  overdue: { label: 'Overdue', color: 'bg-red-100 text-red-800', icon: AlertTriangle },
  today: { label: 'Do today', color: 'bg-amber-100 text-amber-800', icon: Zap },
  this_week: { label: 'This week', color: 'bg-blue-100 text-blue-800', icon: Clock },
  later: { label: 'Later', color: 'bg-slate-100 text-slate-700', icon: MoonStar },
  blocked: { label: 'Blocked', color: 'bg-purple-100 text-purple-800', icon: Flag },
};

const fmtDue = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const MyDayPage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [openTaskId, setOpenTaskId] = useState(null);
  const [weightsOpen, setWeightsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([myDay(), listUsers().catch(() => ({ data: [] }))]);
      setData(d);
      setUsers(u.data || []);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Failed to load My Day', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const userLookup = useMemo(() => {
    const m = {}; users.forEach((u) => { m[u.user_id] = u; }); return m;
  }, [users]);

  const doClaim = async (id) => {
    try {
      await claimTask(id);
      toast({ title: 'Claimed — it\'s yours now' });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Claim failed', variant: 'destructive' }); }
  };

  const doStartStop = async (t) => {
    try {
      if (t.active_timer) {
        const r = await stopTimer(t.id);
        toast({ title: `Timer stopped · ${r.minutes} min logged` });
      } else {
        await startTimer(t.id);
        toast({ title: 'Timer started' });
      }
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  const doSnoozeDecide = async (taskId, reqId, decision) => {
    try {
      await decideSnooze(taskId, { request_id: reqId, decision });
      toast({ title: `Snooze ${decision}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Failed', variant: 'destructive' }); }
  };

  if (loading) {
    return (
      <div data-testid="my-day-page" className="p-16 bg-white rounded-2xl text-center text-[#0b1a2e]/50">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading your day…
      </div>
    );
  }

  const counts = data?.counts || {};
  const totalOpen = counts.overdue + counts.today + counts.this_week + counts.later;

  return (
    <div data-testid="my-day-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Personal · CRM</span>
          <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>
            My Day
          </h1>
          <p className="mt-1.5 text-sm text-[#0b1a2e]/60">
            {totalOpen === 0
              ? 'Inbox zero — no open tasks. Nice.'
              : `You have ${totalOpen} open task${totalOpen !== 1 ? 's' : ''}${counts.overdue > 0 ? `, ${counts.overdue} overdue` : ''}.`}
            {' · '}Priority mode: <b>{data?.weights?.sort_mode || 'score'}</b>
          </p>
        </div>
        {isSuperAdmin(user) && (
          <button onClick={() => setWeightsOpen(true)} data-testid="weights-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[#0f5c3d]/20 text-sm hover:bg-[#f7fbf8]">
            <Sparkles className="w-4 h-4" /> Tune priority weights
          </button>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6" data-testid="my-day-stats">
        {['overdue', 'today', 'this_week', 'later', 'blocked'].map((k) => {
          const M = BUCKET_META[k];
          const Icon = M.icon;
          return (
            <div key={k} className={`rounded-xl p-3 ${M.color}`}>
              <div className="text-[10px] uppercase tracking-widest opacity-80 flex items-center gap-1">
                <Icon className="w-3 h-3" /> {M.label}
              </div>
              <div className="text-2xl font-semibold mt-0.5">{counts[k] || 0}</div>
            </div>
          );
        })}
      </div>

      {/* Available to claim */}
      {(data?.available_to_claim?.length || 0) > 0 && (
        <div className="bg-white border border-black/5 rounded-2xl p-4 mb-6" data-testid="available-to-claim">
          <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-3 flex items-center gap-1">
            <Hand className="w-3.5 h-3.5" /> Available to claim ({data.available_to_claim.length})
            <span className="ml-2 text-[10px] font-normal text-[#0b1a2e]/50 normal-case tracking-normal">First-come-first-serve within your department</span>
          </div>
          <div className="space-y-2">
            {data.available_to_claim.map((t) => (
              <div key={t.id} className="flex items-center gap-3 p-2 bg-[#f7fbf8] rounded-lg">
                <span className={`w-1.5 h-1.5 rounded-full ${PRI[t.priority]?.color || PRI.medium.color}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#0b1a2e] truncate">{t.title}</div>
                  <div className="text-[11px] text-[#0b1a2e]/60">
                    from {t.created_by_name || 'someone'}
                    {t.due_date && ` · due ${fmtDue(t.due_date)}`}
                    {t.estimated_minutes && ` · ~${Math.round(t.estimated_minutes / 60 * 10) / 10}h`}
                  </div>
                </div>
                <button onClick={() => doClaim(t.id)} data-testid="claim-btn"
                  className="px-3 py-1 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                  Claim
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending snooze decisions */}
      {(data?.pending_snoozes?.length || 0) > 0 && (
        <div className="bg-white border border-black/5 rounded-2xl p-4 mb-6" data-testid="pending-snoozes">
          <div className="text-xs font-semibold text-[#0f5c3d] uppercase tracking-widest mb-3">
            Snooze requests awaiting your decision ({data.pending_snoozes.length})
          </div>
          <div className="space-y-2">
            {data.pending_snoozes.flatMap((t) => (t.snooze_requests || []).filter((r) => r.status === 'pending').map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-2 bg-amber-50 rounded-lg">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#0b1a2e]"><b>{r.requested_by_name}</b> asks to snooze <b>{t.title}</b></div>
                  <div className="text-[11px] text-[#0b1a2e]/60">
                    from {r.from_due_date?.slice(0, 10) || 'unset'} → {r.new_due_date?.slice(0, 10)}
                    {r.reason && ` · "${r.reason}"`}
                  </div>
                </div>
                <button onClick={() => doSnoozeDecide(t.id, r.id, 'approved')}
                  className="px-2.5 py-1 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold">Approve</button>
                <button onClick={() => doSnoozeDecide(t.id, r.id, 'denied')}
                  className="px-2.5 py-1 rounded-full bg-white border border-red-300 text-red-700 text-xs">Deny</button>
              </div>
            )))}
          </div>
        </div>
      )}

      {/* Task buckets */}
      <div className="space-y-6">
        {['overdue', 'today', 'this_week', 'later', 'blocked'].map((bucket) => {
          const items = data?.buckets?.[bucket] || [];
          if (items.length === 0) return null;
          const M = BUCKET_META[bucket];
          const Icon = M.icon;
          return (
            <div key={bucket} className="bg-white border border-black/5 rounded-2xl overflow-hidden" data-testid={`bucket-${bucket}`}>
              <div className={`px-4 py-2 border-b border-black/5 flex items-center gap-2 ${M.color}`}>
                <Icon className="w-4 h-4" />
                <span className="text-sm font-semibold">{M.label}</span>
                <span className="text-xs opacity-70">· {items.length}</span>
              </div>
              <div className="divide-y divide-black/5">
                {items.map((t) => (
                  <div key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-[#f7fbf8]" data-testid="my-day-task">
                    <span className={`w-1.5 h-1.5 rounded-full ${PRI[t.priority]?.color || PRI.medium.color}`} />
                    <button onClick={() => setOpenTaskId(t.id)} className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-medium text-[#0b1a2e] truncate">{t.title}</div>
                      <div className="text-[11px] text-[#0b1a2e]/60 flex flex-wrap items-center gap-2">
                        <span>from {t.created_by_name}</span>
                        {t.due_date && <span>· due {fmtDue(t.due_date)}</span>}
                        {t.estimated_minutes && <span>· ~{Math.round(t.estimated_minutes / 60 * 10) / 10}h</span>}
                        {t._meta?.blocking_count > 0 && (
                          <span className="text-[#0f5c3d] font-semibold">· blocks {t._meta.blocking_count}</span>
                        )}
                        {t._meta?.blocked && (
                          <span className="text-purple-700 font-semibold">· waiting on {(t.depends_on_task_ids || []).length}</span>
                        )}
                      </div>
                    </button>
                    <button onClick={() => doStartStop(t)} data-testid="timer-btn"
                      className={`p-1.5 rounded-full ${t.active_timer ? 'bg-red-600 text-white' : 'bg-white border border-[#0f5c3d]/20 text-[#0f5c3d]'} hover:opacity-80`}
                      title={t.active_timer ? 'Stop timer' : 'Start timer'}>
                      {t.active_timer ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    </button>
                    <button onClick={() => setOpenTaskId(t.id)} className="text-[#0b1a2e]/40 hover:text-[#0b1a2e]">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {totalOpen === 0 && !counts.blocked && (
        <div className="p-16 bg-white rounded-2xl text-center border border-black/5">
          <Sun className="w-10 h-10 mx-auto mb-3 text-amber-500" />
          <div className="text-lg font-medium text-[#0b1a2e]">You're all clear</div>
          <p className="text-sm text-[#0b1a2e]/60 mt-1">No open tasks assigned to you. Take a break — or pick something from the team backlog.</p>
        </div>
      )}

      {openTaskId && (
        <TaskDetailDialog taskId={openTaskId} onClose={() => setOpenTaskId(null)} onChanged={load} />
      )}

      {weightsOpen && (
        <WeightsModal onClose={() => setWeightsOpen(false)} onSaved={load} />
      )}
    </div>
  );
};

const WeightsModal = ({ onClose, onSaved }) => {
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getPriorityWeights().then(setW).catch(() => setW({ priority: 4, urgency: 3, dependency: 2, assignor_role: 1, sort_mode: 'score' }));
  }, []);
  if (!w) return null;
  const save = async () => {
    setSaving(true);
    try {
      await setPriorityWeights(w);
      toast({ title: 'Weights updated' });
      onSaved();
      onClose();
    } catch { toast({ title: 'Save failed', variant: 'destructive' }); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-black/5 bg-[#0b1a2e] text-white flex items-center justify-between">
          <div className="text-lg font-medium">Priority weights</div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">✕</button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-1">Sort mode</div>
            <select value={w.sort_mode} onChange={(e) => setW({ ...w, sort_mode: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded">
              <option value="score">Composite score (weights below)</option>
              <option value="simple">Simple — overdue → priority → oldest</option>
            </select>
          </label>
          {w.sort_mode === 'score' && (
            <>
              <WeightRow label="Priority weight" value={w.priority} onChange={(v) => setW({ ...w, priority: v })} />
              <WeightRow label="Urgency weight" value={w.urgency} onChange={(v) => setW({ ...w, urgency: v })} />
              <WeightRow label="Dependency weight (blocks others)" value={w.dependency} onChange={(v) => setW({ ...w, dependency: v })} />
              <WeightRow label="Assignor role weight" value={w.assignor_role} onChange={(v) => setW({ ...w, assignor_role: v })} />
            </>
          )}
        </div>
        <div className="px-5 py-3 bg-[#f7fbf8] border-t border-black/5 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="weights-save"
            className="px-4 py-1.5 rounded-full bg-[#0f5c3d] text-white text-sm font-semibold">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WeightRow = ({ label, value, onChange }) => (
  <label className="block">
    <div className="text-xs text-[#0b1a2e] mb-1 flex justify-between">
      <span>{label}</span><b>{value}</b>
    </div>
    <input type="range" min="0" max="10" value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))}
      className="w-full accent-[#0f5c3d]" />
  </label>
);

export default MyDayPage;
